import React, { useEffect, useState } from "react";
import { Truck, Trash2 } from "lucide-react";
import { Input, Popconfirm } from "antd";
import { useLocation } from "react-router-dom";

import {
  useAllOrders,
  useCancelOrder,
  useCourierPartners,
  useSearchOrders,
} from "../../../../../api/orders/orders";
import CourierModal from "../../../../../component/CourierModal";
import { generateCourierPayload } from "../../../../../utils/helpers";

const ORDERS_PER_PAGE = 20;

const Shipping = ({ status = "pending" }) => {
  const location = useLocation();
  const newOrderId = location.state?.newOrderId || null;

  const [modalVisible, setModalVisible] = useState(!!newOrderId);
  const [selectedOrderId, setSelectedOrderId] = useState(newOrderId);
  const [selectedOrderData, setSelectedOrderData] = useState(null);
  const [courierPayload, setCourierPayload] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, refetch } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    status,
  });
  const { mutate: cancelOrder } = useCancelOrder();

  const { data: courierRates, isLoading: courierLoading } =
    useCourierPartners(courierPayload);

  const { data: searchOrdersData, isLoading: isSearchLoading } =
    useSearchOrders(searchQuery, status);

  useEffect(() => {
    if (!searchQuery && currentPage !== 1) {
      refetch();
    }
  }, [searchQuery, refetch, currentPage]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  const orders = searchQuery ? searchOrdersData || [] : data?.orders || [];

  const filteredOrders = status
    ? orders.filter((order) => order.shippingStatus === status)
    : orders;

  const totalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * ORDERS_PER_PAGE,
    currentPage * ORDERS_PER_PAGE
  );

  const openCourierModal = (order) => {
    setSelectedOrderId(order.shipcluesId);
    setSelectedOrderData(order);
    const payload = generateCourierPayload(order);
    setCourierPayload(payload);
    setModalVisible(true);
  };

  const isLoadingCombined = isLoading || (searchQuery && isSearchLoading);

  return (
    <div className="w-full mt-6">
      {/* Search Input */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Orders Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1000px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order ID / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer Name / Contact</th>
              <th className="p-3">Address</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoadingCombined ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-400">
                  No orders found.
                </td>
              </tr>
            ) : (
              paginatedOrders.map((order, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.order_number}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatus === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatus === "in_transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatus === "shipped"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatus}
                    </div>
                  </td>
                  <td className="p-4">
                    <p>₹{order.total_amount}</p>
                    <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-1 rounded mt-1">
                      {order.PaymentType}
                    </span>
                  </td>
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.phone}</p>
                  </td>
                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.city}</p>
                    <p>{order.BillingAddress?.state}</p>
                    <p>{order.BillingAddress?.pincode}</p>
                  </td>
                  <td className="p-4 flex space-x-2">
                    <button
                      className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded"
                      onClick={() => openCourierModal(order)}
                    >
                      <Truck size={16} />
                    </button>
                    <Popconfirm
                      title="Are you sure you want to cancel this order?"
                      onConfirm={() => cancelOrder(order.shipcluesId)}
                      okText="Yes"
                      cancelText="No"
                      placement="topRight"
                    >
                      <button className="bg-red-500 hover:bg-red-600 text-white p-2 rounded">
                        <Trash2 size={16} />
                      </button>
                    </Popconfirm>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoadingCombined && filteredOrders.length > 0 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Courier Modal */}
      {modalVisible && (
        <CourierModal
          orderId={selectedOrderId}
          order={selectedOrderData}
          courierOptions={courierRates}
          loading={courierLoading}
          onClose={() => setModalVisible(false)}
          onShipped={() => {
            setModalVisible(false);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default Shipping;
